import { Upload } from 'lucide-react';
import React, { useState, useRef } from 'react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { Api } from '../../core/api';
import { injectTSDI } from '../../core/tsdi';
import {
    WorkoutTypeV2,
    WorkoutTypeMappingBatchUpdateResponseDto
} from '../../core/api/FitnessActivityClient';

interface ImportCsvModalProps {
    open: boolean;
    onClose(): void;
    onSuccess(): void;
}

interface ParsedRow {
    rawType: string;
    workoutType: string;
    rejected: boolean;
    error?: string;
}

const VALID_WORKOUT_TYPES = new Set(Object.values(WorkoutTypeV2));

function parseCsvLine(line: string): string[] {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
        const char = line[i];
        if (inQuotes) {
            if (char === '"' && line[i + 1] === '"') {
                current += '"';
                i += 2;
            } else if (char === '"') {
                inQuotes = false;
                i++;
            } else {
                current += char;
                i++;
            }
        } else {
            if (char === '"') {
                inQuotes = true;
                i++;
            } else if (char === ',') {
                fields.push(current.trim());
                current = '';
                i++;
            } else {
                current += char;
                i++;
            }
        }
    }
    fields.push(current.trim());
    return fields;
}

function parseCsv(text: string): ParsedRow[] {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];

    const header = parseCsvLine(lines[0]);
    if (header[0] !== 'rawType' || header[1] !== 'workoutType') {
        return [
            {
                rawType: '',
                workoutType: '',
                rejected: false,
                error: 'Invalid CSV header. Expected: rawType,workoutType[,rejected]'
            }
        ];
    }

    return lines.slice(1).map((line) => {
        const parts = parseCsvLine(line);
        const rawType = parts[0] || '';
        const workoutType = parts[1] || '';
        const rejected = parts[2]?.toLowerCase() === 'true';

        let error: string | undefined;
        if (!rawType) {
            error = 'Missing rawType';
        } else if (
            workoutType &&
            !VALID_WORKOUT_TYPES.has(workoutType as WorkoutTypeV2)
        ) {
            error = `Invalid workoutType: ${workoutType}`;
        }

        return { rawType, workoutType, rejected, error };
    });
}

export const ImportCsvModal = ({
    open,
    onClose,
    onSuccess
}: ImportCsvModalProps) => {
    const { fitnessActivityClient } = injectTSDI(Api);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [response, setResponse] =
        useState<WorkoutTypeMappingBatchUpdateResponseDto | null>(null);

    const hasParseErrors = parsedRows.some((r) => r.error);
    const validRows = parsedRows.filter((r) => !r.error);

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const resetForm = () => {
        setSelectedFile(null);
        setParsedRows([]);
        setError(null);
        setResponse(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            setError('Please select a CSV file');
            return;
        }

        setSelectedFile(file);
        setError(null);
        setResponse(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const rows = parseCsv(text);
            if (rows.length === 0) {
                setError('CSV file is empty or has no data rows');
                setParsedRows([]);
                return;
            }
            setParsedRows(rows);
        };
        reader.readAsText(file);
    };

    const handleSubmit = async () => {
        if (hasParseErrors || validRows.length === 0) return;

        try {
            setLoading(true);
            setError(null);

            const data = validRows.map((row) => ({
                rawType: row.rawType,
                workoutType: row.workoutType
                    ? (row.workoutType as WorkoutTypeV2)
                    : undefined,
                rejected: row.rejected
            }));

            const result =
                await fitnessActivityClient.admin.workoutTypeMappingAdminControllerBatchUpdate(
                    data
                );

            if (result.ok) {
                setResponse(result.data);
                onSuccess();
            } else {
                throw new Error('Failed to process batch update');
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to process batch update');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
            <DialogContent className="sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Import Workout Type Mappings</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                    {response && (
                        <Alert variant="success">
                            <AlertDescription>
                                Batch update complete: {response.created}{' '}
                                created, {response.updated} updated
                            </AlertDescription>
                        </Alert>
                    )}

                    <p className="text-sm text-muted-foreground">
                        Upload a CSV file with columns: rawType, workoutType[,
                        rejected]. Empty workoutType will unmap. rejected=true
                        will reject activities with that raw type.
                    </p>

                    {!response && (
                        <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 text-center">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                onChange={handleFileChange}
                                className="hidden"
                                id="import-csv-file-input"
                                disabled={loading}
                            />
                            <Button
                                variant="outline"
                                disabled={loading}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Upload />
                                {selectedFile
                                    ? 'Change File'
                                    : 'Select CSV File'}
                            </Button>
                            {selectedFile && (
                                <p className="text-sm text-muted-foreground">
                                    Selected:{' '}
                                    <strong>{selectedFile.name}</strong> (
                                    {(selectedFile.size / 1024).toFixed(2)} KB)
                                </p>
                            )}
                        </div>
                    )}

                    {parsedRows.length > 0 && !response && (
                        <div>
                            <p className="mb-2 text-sm font-medium">
                                Preview ({validRows.length} valid
                                {hasParseErrors &&
                                    `, ${parsedRows.length - validRows.length} with errors`}
                                )
                            </p>
                            <div className="max-h-[300px] overflow-auto rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Raw Type</TableHead>
                                            <TableHead>Workout Type</TableHead>
                                            <TableHead>Rejected</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {parsedRows.map((row, index) => (
                                            <TableRow key={index}>
                                                <TableCell className="font-mono">
                                                    {row.rawType}
                                                </TableCell>
                                                <TableCell>
                                                    {row.workoutType || (
                                                        <Badge variant="warning">
                                                            UNMAPPED
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {row.rejected && (
                                                        <Badge variant="destructive">
                                                            REJECTED
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {row.error ? (
                                                        <Badge variant="destructive">
                                                            {row.error}
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="success">
                                                            OK
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        {response ? 'Close' : 'Cancel'}
                    </Button>
                    {!response && (
                        <Button
                            onClick={handleSubmit}
                            loading={loading}
                            disabled={hasParseErrors || validRows.length === 0}
                        >
                            Import {validRows.length} Mappings
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
